import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import BusinessCard from "@/components/BusinessCard";
import { ROICalculatorWidget } from "@/pages/ROICalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ArrowRight, Mail, Calculator, Store, Star, MapPin, Wrench, UtensilsCrossed, Car, Stethoscope, Scissors, ShoppingBag, Dumbbell, Coffee, Wind, Droplets, TrendingUp, Home as HomeIcon, Key, HardHat, Zap, Trees, Sparkles, Paintbrush, Warehouse, Bug } from "lucide-react";
import AiSearchExplainer from "@/components/AiSearchExplainer";

const PROMO_TILES = [
  { to: "/grow-my-business", label: "More leads & sales", desc: "For realtors, agents & local businesses", Icon: TrendingUp, tag: "For business owners" },
  { to: "/hvac", label: "HVAC companies", desc: "More service calls & installs", Icon: Wind, tag: "For business owners" },
  { to: "/plumbers", label: "Plumbers", desc: "More jobs & emergency calls", Icon: Droplets, tag: "For business owners" },
  { to: "/roofers", label: "Roofers", desc: "More roofing jobs & inspections", Icon: HardHat, tag: "For business owners" },
  { to: "/electricians", label: "Electricians", desc: "More electrical service calls", Icon: Zap, tag: "For business owners" },
  { to: "/landscapers", label: "Landscapers", desc: "More lawn & landscape clients", Icon: Trees, tag: "For business owners" },
  { to: "/painters", label: "Painters", desc: "More painting jobs & estimates", Icon: Paintbrush, tag: "For business owners" },
  { to: "/garage-door", label: "Garage Door", desc: "More repairs & new installs", Icon: Warehouse, tag: "For business owners" },
  { to: "/pest-control", label: "Pest Control", desc: "More jobs & recurring plans", Icon: Bug, tag: "For business owners" },
  { to: "/buy-a-home", label: "Buy a home", desc: "Get matched with a trusted team", Icon: Key, tag: "For homeowners" },
  { to: "/sell-your-home", label: "Sell your home", desc: "Sell for more, with less hassle", Icon: HomeIcon, tag: "For homeowners" },
];

const CAT_TILES = [
  { slug: "home-services", label: "Home Services", Icon: Wrench },
  { slug: "restaurants", label: "Dining", Icon: UtensilsCrossed },
  { slug: "automotive", label: "Automotive", Icon: Car },
  { slug: "health-and-medical", label: "Health", Icon: Stethoscope },
  { slug: "beauty-and-spas", label: "Beauty & Spas", Icon: Scissors },
  { slug: "shopping", label: "Shopping", Icon: ShoppingBag },
  { slug: "active-life-and-fitness", label: "Fitness", Icon: Dumbbell },
  { slug: "food-and-drink", label: "Food & Drink", Icon: Coffee },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [states, setStates] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [popularCities, setPopularCities] = useState([]);

  useEffect(() => {
    api.get("/states").then(({ data }) => setStates(data));
    api.get("/businesses", { params: { limit: 8, sort: "rating" } }).then(({ data }) => setFeatured(data));
    api.get("/popular-cities", { params: { limit: 10 } }).then(({ data }) => setPopularCities(data)).catch(() => {});
  }, []);

  const search = () => {
    const p = new URLSearchParams();
    if (q) p.set("search", q);
    if (state !== "all") p.set("state", state);
    navigate(`/browse?${p.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-sam-navy text-sam-cream sam-grain">
        <div className="absolute inset-0 sam-grain opacity-40" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold tracking-wide backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-sam-crimson animate-pulse" /> Proudly local · Real neighbor savings
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Find hometown businesses.<br /><span className="text-sam-crimson">Save</span> like a neighbor.
            </h1>
            <p className="mt-5 text-lg text-sam-cream/70 max-w-xl leading-relaxed">
              Discover trusted local businesses, read real reviews, and grab deals featured on our 9×12 postcard mailer reaching 10,000 homes.
            </p>

            <div className="mt-5 inline-flex items-start gap-2.5 rounded-xl border border-sam-amber/30 bg-sam-amber/10 px-4 py-2.5 max-w-xl backdrop-blur-sm" data-testid="hero-geo-ai">
              <Sparkles className="h-5 w-5 text-sam-amber shrink-0 mt-0.5" />
              <p className="text-sm text-sam-cream/90 leading-snug">
                <span className="font-bold text-sam-cream">New, found by AI:</span> Getting listed puts your business on <span className="font-bold text-sam-cream">GEO &amp; AI search</span>, where neighbors now ask AI to find local businesses and home services near them.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-2 rounded-2xl bg-white p-2 shadow-2xl max-w-2xl">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search className="h-5 w-5 text-sam-slate shrink-0" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Plumber, coffee, dentist…" data-testid="hero-search-input"
                  className="border-0 shadow-none focus-visible:ring-0 text-sam-navy placeholder:text-slate-400 text-base" />
              </div>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger className="sm:w-44 border-0 bg-sam-cream text-sam-navy font-semibold" data-testid="hero-state-select">
                  <SelectValue placeholder="All states" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  {states.map((s) => <SelectItem key={s.abbr} value={s.abbr}>{s.name}{s.launch_status === "Active co-op routes" ? " ✦" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button className="bg-sam-crimson hover:bg-sam-crimson/90 h-11 px-6 font-semibold" onClick={search} data-testid="hero-search-btn">
                Search
              </Button>
            </div>
            <p className="mt-2 text-xs text-sam-cream/40">✦ = live co-op postcard routes available now</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="bg-sam-crimson text-white hover:bg-sam-crimson/90 font-semibold h-11 px-5" data-testid="hero-see-plans-btn">
                <Link to="/plans"><Sparkles className="h-4 w-4 mr-2" />See Plans</Link>
              </Button>
              <Button asChild className="bg-sam-amber text-sam-navy hover:bg-sam-amber/90 font-semibold h-11 px-5" data-testid="hero-plans-btn">
                <Link to="/mailer"><Mail className="h-4 w-4 mr-2" />Advertise on the mailer</Link>
              </Button>
              <Button asChild variant="outline" className="border-sam-cream/30 bg-white/5 text-sam-cream hover:bg-white/10 hover:text-sam-cream font-semibold h-11 px-5" data-testid="hero-roi-btn">
                <Link to="/calculator"><Calculator className="h-4 w-4 mr-2" />Calculate your ROI</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About / mission band */}
      <section className="relative bg-sam-navy2 text-sam-cream border-t border-white/10" data-testid="home-about-band">
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-14 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber">
            <Sparkles className="h-4 w-4" />Who we are
          </span>
          <p className="mt-5 font-display text-xl sm:text-2xl font-semibold leading-relaxed text-sam-cream/90">
            <span className="text-sam-crimson font-extrabold">Saving America Money</span> is a premier growth agency dedicated to empowering independent businesses, local economies, and ambitious agency owners. We bridge the gap between high-impact physical marketing and enterprise-level digital automation, a seamless, start-to-finish infrastructure that removes operational friction and drives measurable revenue.
          </p>
          <p className="mt-4 text-base text-sam-cream/70 leading-relaxed max-w-3xl mx-auto">
            From dominating a local ZIP code with premium direct mail, to automating your digital storefront, to a secure branded portal for your customers and staff, we build the systems that let people and businesses <span className="font-semibold text-sam-cream">be great, be amazing, and be wealthy.</span>
          </p>
          <Link to="/about" data-testid="about-learn-more" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-sam-amber hover:gap-2.5 transition-all">
            Learn more about us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-sam-navy">Browse by category</h2>
            <p className="text-sam-slate mt-1">From your kitchen sink to your morning coffee.</p>
          </div>
          <Link to="/browse" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-sam-crimson hover:gap-2 transition-all">
            All categories <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CAT_TILES.map((c, i) => (
            <motion.button key={c.slug}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/browse?parent=${c.slug}`)} data-testid={`cat-tile-${c.slug}`}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 hover:border-sam-crimson hover:shadow-lg transition-all text-left">
              <span className="grid place-items-center h-10 w-10 rounded-lg bg-sam-navy/5 text-sam-crimson group-hover:bg-sam-crimson group-hover:text-white transition-colors"><c.Icon className="h-5 w-5" /></span>
              <span className="font-display font-bold text-sam-navy group-hover:text-sam-crimson transition-colors">{c.label}</span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-4">
        <div className="flex items-center gap-2 mb-6">
          <Star className="h-5 w-5 fill-sam-amber text-sam-amber" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-sam-navy">Featured local picks</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
        </div>
      </section>

      {/* Grow your business promo */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16" data-testid="home-promo-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-sam-navy">More ways we help your neighborhood</h2>
            <p className="text-sam-slate mt-1">Need more customers, or buying and selling a home? We've got a page for that.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROMO_TILES.map((t, i) => (
            <motion.div key={t.to}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={t.to} data-testid={`promo-tile-${t.to.replace(/\//g, "")}`}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 hover:border-sam-crimson hover:shadow-lg transition-all">
                <div className="flex items-center justify-between">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-sam-navy/5 text-sam-crimson group-hover:bg-sam-crimson group-hover:text-white transition-colors"><t.Icon className="h-5 w-5" /></span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-sam-slate">{t.tag}</span>
                </div>
                <div className="mt-4 font-display font-bold text-lg text-sam-navy group-hover:text-sam-crimson transition-colors">{t.label}</div>
                <p className="text-sm text-sam-slate mt-1 flex-1">{t.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sam-crimson group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <AiSearchExplainer />

      {/* Popular cities */}
      {popularCities.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12">
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="h-5 w-5 text-sam-crimson" />
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-sam-navy">Popular local markets</h2>
          </div>
          <div className="flex flex-wrap gap-2.5" data-testid="home-popular-cities">
            {popularCities.map((c) => (
              <Link key={`${c.state_abbr}-${c.slug}`} to={`/city/${c.slug}?state=${c.state_abbr}`} data-testid={`popular-city-${c.slug}`}
                className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 hover:border-sam-crimson hover:shadow-md transition-all">
                <span className="font-display font-bold text-sam-navy group-hover:text-sam-crimson transition-colors">{c.name}, {c.state_abbr}</span>
                <span className="text-xs font-mono text-sam-slate">{c.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Postcard CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8 items-center rounded-3xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-12 overflow-hidden relative">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-sam-crimson/20 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Mail className="h-4 w-4" />Premium Postcard Mailer</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold leading-tight">12 businesses. One mailbox. 10,000 homes.</h2>
            <p className="mt-4 text-sam-cream/70 leading-relaxed">
              Share a 9×12 landscape postcard with 11 other local companies. Grab the premium 4×9 hero spot for {"$1,800"}, or a 4×6 or 4×3 slot. Every ad includes a customized app — generate high-converting ads, capture leads, automate follow-ups, book appointments, and manage your entire client funnel, all from one AI-powered platform.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button className="bg-sam-crimson hover:bg-sam-crimson/90 font-semibold" onClick={() => navigate("/postcards")} data-testid="cta-postcards"><Mail className="h-4 w-4 mr-2" />See Postcard Routes</Button>
              <Button variant="outline" className="border-white/30 text-sam-cream hover:bg-white/10 hover:text-white font-semibold bg-transparent" onClick={() => navigate("/dashboard")} data-testid="cta-list"><Store className="h-4 w-4 mr-2" />List My Business</Button>
            </div>
          </div>
          <div className="relative rounded-xl bg-sam-navy/60 p-2 border border-white/10 flex flex-col gap-1.5" style={{ aspectRatio: "12/9" }}>
            {/* TOP: A (4x9, 75%) + B (4x3, 25%) */}
            <div className="flex gap-1.5" style={{ flex: "0 0 44%" }}>
              <div style={{ flex: "0 0 75%" }} className="relative rounded-md bg-gradient-to-br from-sam-crimson to-red-800 grid place-items-center text-center">
                <span className="absolute top-1 left-1 grid place-items-center h-4 w-4 rounded-full bg-white/90 text-sam-navy text-[9px] font-extrabold">A</span>
                <span className="font-display font-extrabold text-xs text-white">4×9 · $1,800</span>
              </div>
              <div className="relative flex-1 rounded-md bg-white/10 grid place-items-center">
                <span className="absolute top-1 left-1 grid place-items-center h-4 w-4 rounded-full bg-white/90 text-sam-navy text-[9px] font-extrabold">B</span>
                <span className="text-[9px] font-mono text-white/70">4×3</span>
              </div>
            </div>
            {/* BAND */}
            <div className="bg-white/90 rounded-sm flex items-center justify-center gap-1" style={{ flex: "0 0 11%" }}>
              <span className="font-display font-extrabold text-[9px] sm:text-[11px]"><span className="text-sam-crimson">Saving</span> <span className="text-sam-navy">America Money</span></span>
            </div>
            {/* BOTTOM: C (4x6, 50%) + D + E (4x3, 25% each) */}
            <div className="flex gap-1.5" style={{ flex: "0 0 44%" }}>
              <div style={{ flex: "0 0 50%" }} className="relative rounded-md bg-white/10 grid place-items-center">
                <span className="absolute top-1 left-1 grid place-items-center h-4 w-4 rounded-full bg-white/90 text-sam-navy text-[9px] font-extrabold">C</span>
                <span className="text-[9px] font-mono text-white/70">4×6</span>
              </div>
              {["D", "E"].map((l) => (
                <div key={l} className="relative flex-1 rounded-md bg-white/5 grid place-items-center">
                  <span className="absolute top-1 left-1 grid place-items-center h-4 w-4 rounded-full bg-white/90 text-sam-navy text-[9px] font-extrabold">{l}</span>
                  <span className="text-[9px] font-mono text-white/60">4×3</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-8">
        <ROICalculatorWidget heading />
      </section>
    </div>
  );
}
